import React, { useEffect, useState, useCallback } from 'react';
import { Table, Button, Breadcrumb, Card, Row, Col, Input, Tag, Select, message, Tooltip, Progress } from 'antd';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
    TrophyOutlined,
    SearchOutlined,
    ArrowLeftOutlined,
    CalendarOutlined,
    TeamOutlined,
    DollarOutlined,
    RightOutlined,
    CrownOutlined,
    EnvironmentOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    ClockCircleOutlined,
    CarOutlined
} from '@ant-design/icons';
import { rankingsService } from '../../common/services';
import dayjs from 'dayjs';

const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount || 0) + ' FCFA';
};

const formatQuarterShort = (q) => {
    if (!q) return '';
    const match = q.match(/^(\d{4})-T(\d)$/);
    if (!match) return q;
    return `T${match[2]} ${match[1]}`;
};

const formatQuarterFull = (q) => {
    if (!q) return '';
    const match = q.match(/^(\d{4})-T(\d)$/);
    if (!match) return q;
    const quarterNames = { '1': 'Février - Avril', '2': 'Mai - Juillet', '3': 'Août - Octobre', '4': 'Novembre - Janvier' };
    return `Trimestre ${match[2]} ${match[1]} (${quarterNames[match[2]]})`;
};

const formatMonth = (m) => {
    if (!m) return '';
    const [, month] = m.split('-');
    const names = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
    return names[parseInt(month) - 1];
};

const getRankBadgeStyle = (rank) => {
    if (rank === 1) return { background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)', color: 'white' };
    if (rank === 2) return { background: 'linear-gradient(135deg, #C0C0C0 0%, #A0A0A0 100%)', color: 'white' };
    if (rank === 3) return { background: 'linear-gradient(135deg, #CD7F32 0%, #A0522D 100%)', color: 'white' };
    if (rank <= 5) return { background: '#722ED1', color: 'white' };
    return { background: '#f0f0f0', color: '#666' };
};

const QuarterlyRankingsDetail = () => {
    const { period } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const cityFromUrl = searchParams.get('city');

    const [rankings, setRankings] = useState([]);
    const [top5, setTop5] = useState([]);
    const [stats, setStats] = useState(null);
    const [criteria, setCriteria] = useState(null);
    const [months, setMonths] = useState([]);
    const [cities, setCities] = useState([]);
    const [selectedCity, setSelectedCity] = useState(cityFromUrl || null);
    const [isLoading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0 });

    const loadCities = useCallback(async () => {
        try {
            const response = await rankingsService.getCities();
            if (response.success) {
                const loadedCities = response.data.cities || [];
                setCities(loadedCities);
                if (!selectedCity) {
                    const defaultCity = loadedCities.find(c => c.isDefault);
                    if (defaultCity) {
                        setSelectedCity(defaultCity._id);
                        setSearchParams({ city: defaultCity._id });
                    }
                }
            }
        } catch (error) {
            console.error('Erreur chargement villes:', error);
        }
    }, []);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                page: pagination.page,
                limit: pagination.limit,
                search
            };
            if (selectedCity) params.city = selectedCity;

            const response = await rankingsService.getQuarterlyRankingByPeriod(period, params);

            if (response.success) {
                setRankings(response.data.rankings || []);
                setTop5(response.data.top5 || []);
                setStats(response.data.stats);
                setCriteria(response.data.criteria);
                setMonths(response.data.months || []);
                setPagination(prev => ({
                    ...prev,
                    total: response.data.pagination?.total || 0
                }));
            }
        } catch (error) {
            message.error('Erreur lors du chargement');
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [period, pagination.page, pagination.limit, search, selectedCity]);

    useEffect(() => { loadCities(); }, [loadCities]);
    useEffect(() => { loadData(); }, [loadData]);

    const handleCityChange = (value) => {
        setSelectedCity(value);
        setPagination(prev => ({ ...prev, page: 1 }));
        if (value) {
            setSearchParams({ city: value });
        } else {
            setSearchParams({});
        }
    };

    const handleTableChange = (pag) => {
        setPagination(prev => ({
            ...prev,
            page: pag.current,
            limit: pag.pageSize
        }));
    };

    const handleSearch = (value) => {
        setSearch(value);
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const columns = [
        {
            title: 'Rang',
            key: 'rank',
            width: 80,
            render: (_, record) => (
                record.isEligible && record.rank > 0 ? (
                    <div
                        style={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 'bold',
                            fontSize: 16,
                            ...getRankBadgeStyle(record.rank)
                        }}
                    >
                        {record.rank}
                    </div>
                ) : (
                    <span style={{ color: '#ccc' }}>-</span>
                )
            )
        },
        {
            title: 'Chauffeur',
            key: 'driver',
            render: (_, record) => (
                <div>
                    <div style={{ fontWeight: 600 }}>
                        {record.firstName || 'Anonyme'} {record.lastName || ''}
                        {record.isTop5 && (
                            <Tag color="purple" style={{ marginLeft: 8 }}>
                                <CrownOutlined /> Top 5
                            </Tag>
                        )}
                        {record.isEligible && !record.isTop5 && (
                            <Tag color="green" style={{ marginLeft: 8 }}>
                                <CheckCircleOutlined /> Éligible
                            </Tag>
                        )}
                    </div>
                    <div style={{ color: '#999', fontSize: 13 }}>{record.phone}</div>
                </div>
            )
        },
        {
            title: 'Ville',
            dataIndex: 'cityName',
            key: 'cityName',
            render: (cityName) => cityName ? (
                <Tag icon={<EnvironmentOutlined />} color="blue">{cityName}</Tag>
            ) : '-'
        },
        {
            title: 'Revenus',
            dataIndex: 'totalRevenue',
            key: 'totalRevenue',
            sorter: (a, b) => a.totalRevenue - b.totalRevenue,
            render: (amount) => (
                <span style={{ color: '#D4A853', fontWeight: 700, fontSize: 15 }}>
                    {formatCurrency(amount)}
                </span>
            )
        },
        {
            title: 'Courses',
            dataIndex: 'totalOrders',
            key: 'totalOrders',
            sorter: (a, b) => a.totalOrders - b.totalOrders
        },
        {
            title: 'Heures',
            dataIndex: 'totalSupplyHours',
            key: 'totalSupplyHours',
            sorter: (a, b) => a.totalSupplyHours - b.totalSupplyHours,
            render: (hours) => (
                <span style={{ fontWeight: 600, color: hours >= 130 ? '#52c41a' : '#999' }}>
                    {hours}h
                </span>
            )
        },
        {
            title: 'Éligibilité',
            key: 'eligibility',
            width: 200,
            render: (_, record) => {
                const c = record.eligibilityCriteria;
                if (!c) return '-';
                return (
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        <Tooltip title={`${record.totalOrders}/${c.minOrders} courses`}>
                            <Tag color={c.meetsOrders ? 'green' : 'red'} style={{ fontSize: 10 }}>
                                {c.meetsOrders ? <CheckCircleOutlined /> : <CloseCircleOutlined />} Courses
                            </Tag>
                        </Tooltip>
                        <Tooltip title={`${formatCurrency(record.totalRevenue)} / ${formatCurrency(c.minRevenue)}`}>
                            <Tag color={c.meetsRevenue ? 'green' : 'red'} style={{ fontSize: 10 }}>
                                {c.meetsRevenue ? <CheckCircleOutlined /> : <CloseCircleOutlined />} Revenus
                            </Tag>
                        </Tooltip>
                        <Tooltip title={`${record.totalSupplyHours}h / ${c.minHours}h`}>
                            <Tag color={c.meetsHours ? 'green' : 'red'} style={{ fontSize: 10 }}>
                                {c.meetsHours ? <CheckCircleOutlined /> : <CloseCircleOutlined />} Heures
                            </Tag>
                        </Tooltip>
                    </div>
                );
            }
        },
        {
            title: 'Détail mois',
            key: 'breakdown',
            width: 250,
            render: (_, record) => {
                const breakdown = record.monthlyBreakdown || [];
                if (breakdown.length === 0) return '-';
                return (
                    <div style={{ fontSize: 11 }}>
                        {breakdown.map(b => (
                            <div key={b.month} style={{ display: 'flex', gap: 8, marginBottom: 2 }}>
                                <Tag style={{ minWidth: 35, textAlign: 'center', fontSize: 10 }}>{formatMonth(b.month)}</Tag>
                                <span>{formatCurrency(b.revenue)}</span>
                                <span style={{ color: '#999' }}>{b.orders}c</span>
                                <span style={{ color: '#722ED1' }}>{b.supplyHours}h</span>
                            </div>
                        ))}
                    </div>
                );
            }
        }
    ];

    return (
        <div className="quarterly-rankings-detail-page">
            <Breadcrumb
                separator={<RightOutlined />}
                items={[
                    { title: <Link to="/dashboard">Tableau de bord</Link> },
                    { title: <Link to="/dashboard/quarterly-rankings">Classement Trimestriel</Link> },
                    { title: formatQuarterShort(period) }
                ]}
                style={{ marginBottom: 24 }}
            />

            {/* Back Button & Title */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
                <Button
                    icon={<ArrowLeftOutlined />}
                    onClick={() => navigate('/dashboard/quarterly-rankings')}
                >
                    Retour
                </Button>
                <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <TrophyOutlined style={{ color: '#722ED1' }} />
                    {formatQuarterFull(period)}
                    {selectedCity && cities.find(c => c._id === selectedCity) && (
                        <Tag color="purple" style={{ marginLeft: 12 }}>
                            <EnvironmentOutlined /> {cities.find(c => c._id === selectedCity)?.name}
                        </Tag>
                    )}
                </h2>
            </div>

            {/* Filtre par ville */}
            <Card style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                    <EnvironmentOutlined style={{ fontSize: 20, color: '#722ED1' }} />
                    <span style={{ fontWeight: 600 }}>Filtrer par ville :</span>
                    <Select
                        placeholder="Toutes les villes"
                        style={{ width: 250 }}
                        allowClear
                        value={selectedCity}
                        onChange={handleCityChange}
                        loading={cities.length === 0}
                    >
                        {cities.map(city => (
                            <Select.Option key={city._id} value={city._id}>
                                {city.name} {city.isDefault && '(Par défaut)'}
                            </Select.Option>
                        ))}
                    </Select>
                    {selectedCity && (
                        <Tag color="purple" icon={<EnvironmentOutlined />}>
                            {cities.find(c => c._id === selectedCity)?.name}
                        </Tag>
                    )}
                </div>
            </Card>

            {/* Critères + Stats */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>Chauffeurs analysés</div>
                        <div style={{ fontSize: 24, fontWeight: 700 }}>
                            <TeamOutlined style={{ color: '#722ED1', marginRight: 8 }} />
                            {stats?.totalDrivers || 0}
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>Éligibles</div>
                        <div style={{ fontSize: 24, fontWeight: 700, color: '#52c41a' }}>
                            <CheckCircleOutlined style={{ marginRight: 8 }} />
                            {stats?.eligibleDrivers || 0}
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>Mois inclus</div>
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
                            {months.map(m => (
                                <Tag key={m} color="purple">{formatMonth(m)}</Tag>
                            ))}
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card style={{ background: 'linear-gradient(135deg, #f9f0ff 0%, #fff 100%)' }}>
                        <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>Critères d'éligibilité</div>
                        <div style={{ display: 'flex', gap: 4, flexDirection: 'column' }}>
                            <Tag icon={<CarOutlined />} color="blue">≥ {criteria?.minOrders || 200} courses</Tag>
                            <Tag icon={<DollarOutlined />} color="gold">≥ {formatCurrency(criteria?.minRevenue || 500000)}</Tag>
                            <Tag icon={<ClockCircleOutlined />} color="purple">≥ {criteria?.minHours || 130}h connexion</Tag>
                        </div>
                    </Card>
                </Col>
            </Row>

            {/* Top 5 Super Lots */}
            {top5.length > 0 && (
                <Card
                    title={
                        <span>
                            <CrownOutlined style={{ marginRight: 8, color: '#722ED1' }} />
                            Top 5 - Super Lots
                        </span>
                    }
                    style={{ marginBottom: 24, borderColor: '#722ED1' }}
                >
                    <Row gutter={[12, 12]}>
                        {top5.map((driver) => (
                            <Col xs={24} sm={12} md={8} lg={4.8} key={driver._id} style={{ marginBottom: 12 }}>
                                <Card
                                    size="small"
                                    style={{
                                        borderLeft: `4px solid ${driver.rank <= 3 ? '#722ED1' : '#d9d9d9'}`,
                                        background: driver.rank === 1 ? 'linear-gradient(135deg, #f9f0ff 0%, #fff 100%)' : undefined
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                        <div
                                            style={{
                                                width: 40,
                                                height: 40,
                                                borderRadius: '50%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontWeight: 'bold',
                                                fontSize: 16,
                                                ...getRankBadgeStyle(driver.rank)
                                            }}
                                        >
                                            {driver.rank}
                                        </div>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {driver.firstName} {driver.lastName?.charAt(0)}.
                                            </div>
                                            <div style={{ color: '#D4A853', fontWeight: 600, fontSize: 13 }}>
                                                {formatCurrency(driver.totalRevenue)}
                                            </div>
                                            <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                                                <Tag color="blue" style={{ fontSize: 10 }}>{driver.totalOrders} courses</Tag>
                                                <Tag color="purple" style={{ fontSize: 10 }}>{driver.totalSupplyHours}h</Tag>
                                            </div>
                                        </div>
                                    </div>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                </Card>
            )}

            {/* Full Table */}
            <Card
                title={
                    <span>
                        <TeamOutlined style={{ marginRight: 8 }} />
                        Tous les chauffeurs ({pagination.total})
                    </span>
                }
                extra={
                    <Input.Search
                        placeholder="Rechercher..."
                        allowClear
                        style={{ width: 250 }}
                        onSearch={handleSearch}
                        prefix={<SearchOutlined />}
                    />
                }
            >
                <Table
                    dataSource={rankings}
                    columns={columns}
                    rowKey="_id"
                    loading={isLoading}
                    pagination={{
                        current: pagination.page,
                        pageSize: pagination.limit,
                        total: pagination.total,
                        showSizeChanger: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} sur ${total}`
                    }}
                    onChange={handleTableChange}
                    rowClassName={(record) => {
                        if (record.isTop5) return 'top5-row';
                        if (record.isEligible) return 'eligible-row';
                        return '';
                    }}
                    scroll={{ x: 1200 }}
                />
            </Card>

            <style>{`
                .top5-row {
                    background: linear-gradient(90deg, rgba(114, 46, 209, 0.08) 0%, transparent 100%);
                }
                .eligible-row {
                    background: linear-gradient(90deg, rgba(82, 196, 26, 0.05) 0%, transparent 100%);
                }
            `}</style>
        </div>
    );
};

export default QuarterlyRankingsDetail;
